<!--
  navItem "Bedingungssätze" — the named condition sets of this provision type, laid out as a Review
  Workbench (fe_core_review-workbench). <mvs-review-workbench> is the ONLY layout owner: splitter,
  host height and the height-bound pane columns. Each projected pane root below owns its scroll.

  LEFT  (#workbenchList):   the set table, created ONCE per provision type (R2). Row click selects.
                            The counts ("Bedingungen", "Verwendungen") are custom columns fed from
                            ONE batched QL request.
  RIGHT (#workbenchDetail): the selected set — header (name, alias, counts, warning), "Verwendet
                            von", delete — and its condition table, cleared and rebuilt per selection.

  [panes]="selectedSetId ? 2 : 1": without a selection the set table gets the full width. 1- and
  2-pane render the SAME splitter (the detail pane is collapsed by CSS), so the set table is never
  remounted by a selection or by "Auswahl aufheben" — sorting and scroll survive (R2/R3).

  HEIGHT — level 1, not nested (R5, not R7), in BOTH hosts: the full object view under the app shell
  and the side view in the full-height right p-drawer (?sideNavItem=conditionSets). In both, the
  window bottom is the bottom of the frame, so the host measures itself against the viewport
  (`autoFitViewport`) — measured in the drawer at 1280x800: no drawer scroll, no clipping.
  `viewportBottomGapRem` = the shell's `.layout-content` bottom padding. See the class TSDoc.

  STACKED: in a narrow CONTAINER (the side drawer) with a set selected, `--stacked` puts the detail
  below the list instead of beside it — a class only, the splitter structure never changes (R2/R3).

  HORIZONTAL SCROLL: each pane is the one scroll root on both axes; the set header card, the widget
  header and the paginator are pinned horizontally (sticky left) so only the table moves sideways.
-->
<div class="cp-provision-type-condition-set">

    @if (initialized && setWidget) {

        <div class="flex align-items-start gap-2 mb-3">
            <i class="fa-regular fa-circle-info text-color-secondary mt-1"></i>
            <span class="text-sm text-color-secondary">
                Ein Bedingungssatz legt fest, für welche Verträge eine Zuordnung gilt, und kann von
                mehreren Zuordnungen dieses Provisionstyps gemeinsam verwendet werden. Die
                Bedingungen werden nach Priorität geprüft; die <strong>erste zutreffende</strong>
                entscheidet („Einschließen“ = Zuordnung gilt, „Ausschließen“ = Zuordnung gilt nicht).
                Trifft keine zu, gilt die Zuordnung nicht; ein Satz ohne Bedingungen greift nie.
                Eine Zuordnung ohne Bedingungssatz gilt für alle Verträge.
            </span>
        </div>

        <mvs-review-workbench
                class="cp-condition-set-workbench"
                [class.cp-condition-set-workbench--stacked]="stacked"
                [panes]="selectedSetId ? 2 : 1"
                [panelSizes]="[64, 36]"
                [minSizes]="[30, 28]"
                [autoFitViewport]="true"
                [viewportBottomGapRem]="2">

            <!-- LEFT: the set worklist. Persistent — exactly one instantiation site. -->
            <ng-template #workbenchList>
                <div class="cp-condition-set-pane cp-condition-set-pane--list pr-2">

                    <!-- One error banner only: with a set selected, the detail's usage section
                         carries it. It sits INSIDE the pane so it never moves the host's top edge. -->
                    @if (countsFailed && !selectedSetId) {
                        <div class="cp-condition-set-counts-error flex align-items-center gap-2 mb-3">
                            <p-message severity="error" styleClass="flex-1"
                                       text="Die Anzahl der Bedingungen und Verwendungen konnte nicht geladen werden.">
                            </p-message>
                            <p-button label="Erneut laden" icon="fa-regular fa-rotate-right" [outlined]="true"
                                      size="small" (onClick)="onRetryCounts()">
                            </p-button>
                        </div>
                    }

                    <mvs-widget
                            [widgetData]="setWidget"
                            [importObjectContext]="setImportContext"
                            (onObjectSelect)="onSetSelect($event)"
                            (onLoadData)="onSetDataLoaded()">

                        <ng-template let-object="object" mvs-widget-custom-field-directive
                                     area="columns" [fieldId]="conditionCountFieldId" label="Bedingungen">
                            @if (summaryOf(object); as summary) {
                                <div class="flex align-items-center flex-wrap gap-1">
                                    <span class="font-medium mr-1">{{ summary.conditionCount }}</span>
                                    @if (summary.includeCount) {
                                        <p-tag severity="success" icon="fa-solid fa-plus"
                                               [value]="'' + summary.includeCount"
                                               pTooltip="Einschließen"></p-tag>
                                    }
                                    @if (summary.excludeCount) {
                                        <p-tag severity="danger" icon="fa-solid fa-minus"
                                               [value]="'' + summary.excludeCount"
                                               pTooltip="Ausschließen"></p-tag>
                                    }
                                    @if (!summary.conditionCount) {
                                        <p-tag severity="warn" value="greift nie"></p-tag>
                                    }
                                </div>
                            } @else if (countsFailed) {
                                <span class="text-color-secondary" pTooltip="Konnte nicht geladen werden">–</span>
                            } @else if (!countsLoaded) {
                                <p-skeleton width="3rem" height="1rem"></p-skeleton>
                            }
                        </ng-template>

                        <ng-template let-object="object" mvs-widget-custom-field-directive
                                     area="columns" [fieldId]="usageCountFieldId" label="Verwendungen">
                            @if (countsFailed) {
                                <span class="text-color-secondary" pTooltip="Konnte nicht geladen werden">–</span>
                            } @else if (countsLoaded) {
                                @if (usagesOf(object).length; as usageCount) {
                                    <span>{{ usageCount }}</span>
                                } @else {
                                    <span class="text-color-secondary">nicht verwendet</span>
                                }
                            } @else {
                                <p-skeleton width="2rem" height="1rem"></p-skeleton>
                            }
                        </ng-template>

                    </mvs-widget>
                </div>
            </ng-template>

            <!-- RIGHT: the selected set. The pane is always mounted; only its CONTENT is
                 conditional (R3). In 1-pane mode it is collapsed by CSS. -->
            <ng-template #workbenchDetail>
                <div class="cp-condition-set-pane cp-condition-set-pane--detail pl-2">

                    @if (selectedSetId) {

                        <div class="cp-condition-set-header surface-card border-1 surface-border border-round p-3 mb-3">

                            <div class="flex align-items-start justify-content-between gap-3">
                                <div class="flex flex-column gap-1 min-w-0">
                                    <span class="text-sm text-color-secondary">Bedingungssatz</span>
                                    <div class="flex align-items-center flex-wrap gap-2">
                                        <span class="cp-condition-set-name text-lg font-medium">{{ selectedName }}</span>
                                        @if (selectedAlias) {
                                            <span class="cp-condition-set-alias text-sm text-color-secondary">({{ selectedAlias }})</span>
                                        }
                                    </div>
                                </div>
                                <div class="flex align-items-center flex-shrink-0 gap-2">
                                    <p-button
                                            label="Bedingungssatz löschen"
                                            icon="fa-regular fa-trash-can"
                                            severity="danger"
                                            [outlined]="true"
                                            size="small"
                                            [loading]="deleteBusy"
                                            (onClick)="onDeleteSet()">
                                    </p-button>
                                    <p-button
                                            icon="fa-regular fa-xmark"
                                            [text]="true"
                                            [rounded]="true"
                                            severity="secondary"
                                            size="small"
                                            ariaLabel="Auswahl aufheben"
                                            pTooltip="Auswahl aufheben"
                                            (onClick)="onDetailClose()">
                                    </p-button>
                                </div>
                            </div>

                            <!-- Counts -->
                            <div class="cp-condition-set-counts flex align-items-center flex-wrap gap-2 mt-3">
                                @if (summaryOf(selectedSetId); as summary) {
                                    <p-tag severity="secondary"
                                           [value]="summary.conditionCount + (summary.conditionCount === 1 ? ' Bedingung' : ' Bedingungen')">
                                    </p-tag>
                                    <p-tag severity="success" icon="fa-solid fa-plus"
                                           [value]="summary.includeCount + ' Einschließen'"></p-tag>
                                    <p-tag severity="danger" icon="fa-solid fa-minus"
                                           [value]="summary.excludeCount + ' Ausschließen'"></p-tag>
                                } @else if (!countsLoaded && !countsFailed) {
                                    <p-skeleton width="12rem" height="1.5rem"></p-skeleton>
                                }
                            </div>

                            @if (countsLoaded && summaryOf(selectedSetId)?.conditionCount === 0) {
                                <p-message severity="warn" styleClass="w-full mt-3"
                                           text="Dieser Bedingungssatz enthält keine Bedingungen — jede Zuordnung, die ihn verwendet, greift nie.">
                                </p-message>
                            }

                            <!-- Usages -->
                            <div class="cp-condition-set-usages mt-3">
                                <span class="text-sm font-medium">Verwendet von</span>
                                @if (countsFailed) {
                                    <div class="flex align-items-center gap-2 mt-2">
                                        <p-message severity="error" styleClass="flex-1"
                                                   text="Die Verwendungen konnten nicht geladen werden.">
                                        </p-message>
                                        <p-button label="Erneut laden" icon="fa-regular fa-rotate-right" [outlined]="true"
                                                  size="small" (onClick)="onRetryCounts()">
                                        </p-button>
                                    </div>
                                } @else if (!countsLoaded) {
                                    <p-skeleton height="2rem" styleClass="mt-2"></p-skeleton>
                                } @else if (usagesOf(selectedSetId).length) {
                                    <div class="flex flex-column gap-1 mt-2">
                                        @for (usage of usagesOf(selectedSetId); track usage.entity + '-' + usage.assignmentId) {
                                            <div class="cp-condition-set-usage flex align-items-center flex-wrap gap-2 text-sm">
                                                <p-tag severity="info" [value]="usage.entityLabel"></p-tag>
                                                <span>{{ usage.name }}</span>
                                                @if (usage.alias) {
                                                    <span class="text-color-secondary">({{ usage.alias }})</span>
                                                }
                                            </div>
                                        }
                                    </div>
                                } @else {
                                    <div class="text-sm text-color-secondary mt-2">
                                        Keine Zuordnung verwendet diesen Bedingungssatz.
                                    </div>
                                }
                            </div>

                            @if (deleteError) {
                                <p-message severity="error" styleClass="cp-condition-set-delete-error w-full mt-3"
                                           [text]="deleteError"></p-message>
                            }
                        </div>

                        <!-- Conditions of the selected set -->
                        @if (conditionWidget) {
                            <mvs-widget
                                    [widgetData]="conditionWidget"
                                    [importObjectContext]="conditionImportContext"
                                    (onLoadData)="onConditionDataLoaded()">

                                <ng-template let-object="object" mvs-widget-custom-field-directive
                                             area="columns" [fieldId]="conditionModeFieldId" label="Wirkung">
                                    <p-tag [value]="conditionModeLabel(object)"
                                           [severity]="conditionModeSeverity(object)"
                                           [icon]="conditionModeIcon(object)">
                                    </p-tag>
                                </ng-template>

                            </mvs-widget>
                        } @else {
                            <p-skeleton height="10rem"></p-skeleton>
                        }

                    } @else {
                        <p-message
                                severity="info"
                                text="Bitte einen Bedingungssatz auswählen."
                                styleClass="w-full">
                        </p-message>
                    }

                </div>
            </ng-template>

        </mvs-review-workbench>

    } @else if (initialized) {
        <p-message severity="info" text="Kein Provisionstyp ausgewählt." styleClass="w-full"></p-message>
    } @else {
        <p-skeleton height="12rem"></p-skeleton>
    }

</div>
